import React, { FC, createContext } from 'react'

import { serverSideQuery, useQuery } from '@/api'
import { DEFAULT_CONTEXT_VALUES, PROMO_BY_TRIGGER_ATTR_ENDPOINT } from '@/constants'

import { promoSelector } from './selectors'
import type { PromoContext as Context, PromoProviderProps, PromoResponse, PromoSelectors, PromoState } from './types'

const PromoContext = createContext<Context>({
    ...DEFAULT_CONTEXT_VALUES,
    dispatch: { refetch: () => undefined },
    selectors: { promo: undefined },
})

const { Provider } = PromoContext

/**
 * A provider for managing and accessing state for UTM promo codes and
 * discounted pricing.
 *
 * @param props - The provider props as a {@link PromoProviderProps} object.
 *
 * @returns A rendered provider for which all children may access the
 * {@link PromoContext}.
 */
export const PromoProvider: FC<PromoProviderProps> = ({ children, initialParams, initialProps }) => {
    const { promoTriggerAttr } = initialParams

    const { fetching, data, error, refetch } = useQuery<PromoResponse>({
        endpoint: `${PROMO_BY_TRIGGER_ATTR_ENDPOINT}/${promoTriggerAttr}`,
        initialData: initialProps?.data,
        initialFetchDisabled: !!initialProps?.data,
        enabled: Boolean(promoTriggerAttr),
    })

    const state: PromoState = { fetching, data, error }

    const selectors: PromoSelectors = {
        promo: promoSelector(state),
    }

    return <Provider value={{ state, selectors, dispatch: { refetch } }}>{children}</Provider>
}

export default PromoContext

export const serverSideRead = async ({
    promoTriggerAttr,
}: {
    promoTriggerAttr: string
}): Promise<PromoResponse | undefined> => {
    if (!promoTriggerAttr) return
    const queryInstance = serverSideQuery<PromoResponse>(`${PROMO_BY_TRIGGER_ATTR_ENDPOINT}/${promoTriggerAttr}`)
    const promoData = await queryInstance({})
    return promoData
}
